import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";

// ─────────────────────────────────────────────────────────────────────
// Sentry initialisation — read DSN from REACT_APP_SENTRY_DSN. If unset
// (preview without DSN), Sentry simply no-ops so dev never blocks.
// `window.Sentry` is exposed so our custom ErrorBoundary can hand off
// React render-time errors without a hard import dependency.
// ─────────────────────────────────────────────────────────────────────
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || (process.env.NODE_ENV === "production" ? "production" : "development"),
    // Track 10% of transactions in prod to stay well inside the free-tier event quota.
    tracesSampleRate: 0.1,
    // Capture release information when the build tool injects it; otherwise leave undefined.
    release: process.env.REACT_APP_RELEASE,
    // Don't send PII; the welfare/training data shouldn't reach error events anyway.
    sendDefaultPii: false,
    // Suppress noise from webpack's hot-module-replacement chunks. These only
    // exist in preview/dev builds and fire ReferenceErrors when a renamed
    // symbol is patched in a stale tab — never a real product bug.
    denyUrls: [
      /\.hot-update\.js(\?.*)?$/i,         // hmr patch bundles
      /webpack-internal:/i,                // dev internal source maps
      /chrome-extension:\/\//i,            // user-installed browser extensions
      /^moz-extension:\/\//i,              // firefox extensions
      /^safari-extension:\/\//i,           // safari extensions
    ],
    // Drop common third-party / dev-only error patterns before they're sent.
    ignoreErrors: [
      // Hot-reload symbol-mismatch from renamed functions; resolves on refresh.
      /is not defined.*hot-update/i,
      // ResizeObserver loop limit exceeded — benign browser warning, fires on
      // some autoplaying carousels but never affects rendering.
      /ResizeObserver loop/i,
      // Network errors triggered by the user closing the tab mid-request.
      "Non-Error promise rejection captured",
    ],
    // Belt-and-braces filter: even if a hot-update URL slipped past denyUrls
    // (e.g. event was queued before init), block it at the send hook.
    beforeSend(event, hint) {
      const filename = event?.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)?.[0]?.filename || "";
      if (/\.hot-update\.js/i.test(filename)) return null;
      const msg = hint?.originalException?.message || event?.message || "";
      if (/is not defined/i.test(msg) && /hot-update/i.test(filename)) return null;
      return event;
    },
  });
  // Expose the global so ErrorBoundary's soft-dep handoff works.
  window.Sentry = Sentry;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
